import { isEmpty } from 'lodash';
import { useEffect, useState } from 'react';

import { api } from '../../../api';
import { Voucher } from '../../../api/types/voucher';
import PageError from '../../../common/PageError';
import HubPageLayout from '../../../common/hub/HubPageLayout';
import { Loader } from '../../../components/Loader';
import { PageTitle } from '../../../components/hub/PageTitle';
import RecordsPagination from '../../../components/hub/RecordsPagination';
import { TicketIcon } from '../../../icons/TicketIcon';
import { NO_OF_VOUCHERS_PER_PAGE } from '../../../util/constants';
import { PATH_HUB_HOME } from '../../../util/pagePath';
import { BookOneOffSession } from './BookOneOffSession';
import VoucherFilter from './VoucherFilter';
import { VoucherListItem } from './VoucherListItem';
import useVoucherContext, { withVoucherContextProvider } from './useVoucherContext';

const Vouchers = () => {
  const { vouchers, filteredVouchers, fetchVoucherIsError, fetchVoucherIsLoading } = useVoucherContext();
  const [vouchersInView, setVouchersInView] = useState<Voucher[]>([]);
  const [startIndex, setStartIndex] = useState(0);

  const {
    data: fetchCancelledSessions,
    isLoading: fetchCancelledSessionsIsLoading,
    isError: fetchCancelledSessionsIsError,
  } = api.session.all.useQuery('cancelled');

  const getVouchersInViewByIndex = (sortedVouchers: Voucher[], startIndex: number) => {
    const totalVouchers = sortedVouchers.length;
    if (startIndex >= totalVouchers) {
      return [];
    }

    return sortedVouchers.slice(startIndex, startIndex + NO_OF_VOUCHERS_PER_PAGE);
  };

  useEffect(() => {
    if (filteredVouchers.length > 0) {
      const vouchersInView = getVouchersInViewByIndex(filteredVouchers, 0);
      setVouchersInView((current) => {
        if (current.length === vouchersInView.length && JSON.stringify(current) === JSON.stringify(vouchersInView)) {
          return current;
        }
        return vouchersInView;
      });
    } else {
      setVouchersInView([]);
    }
  }, [filteredVouchers]);

  const handlePaginationClick = (newStartIndex: number) => {
    setVouchersInView(getVouchersInViewByIndex(filteredVouchers, newStartIndex));
    setStartIndex(newStartIndex);
  };

  return (
    <HubPageLayout>
      {fetchVoucherIsLoading || fetchCancelledSessionsIsLoading ? (
        <Loader />
      ) : fetchVoucherIsError || fetchCancelledSessionsIsError ? (
        <PageError activePage="hub-page" retryEnabled={false} />
      ) : (
        <>
          <PageTitle title="Cluey Vouchers" className="pb-6" breadcrumbs={[{ label: 'Home', to: PATH_HUB_HOME }]} />
          <div className="flex flex-col gap-y-6">
            <p>Manage your session vouchers and book make-up sessions</p>
            <div className="flex flex-col gap-4 rounded-lg bg-slate-1 p-6 lg:flex-row">
              <div className="flex flex-row gap-3 lg:flex-row">
                <div className="flex !w-6 items-start justify-center">
                  <TicketIcon width={24} height={24} className="mt-[2px] fill-blood-orange-4" />
                </div>
                <div className="flex flex-col items-start gap-2">
                  <div className="flex items-center gap-3 text-lg font-bold lg:gap-4">
                    <p className="text-xl font-bold">How Cluey Vouchers work</p>
                  </div>
                  <div className="flex flex-col gap-3">
                    <p>
                      Cluey Vouchers are issued when a paid school holiday session is cancelled. Each voucher can be
                      used to book a make-up session and is valid for 75 days from the original session date.
                    </p>
                    <p>
                      You can use your vouchers for any active enrolment on your account. View status, expiry, and
                      original session details here, and book your make-up session directly from this page.
                    </p>
                  </div>
                </div>
              </div>
            </div>
            {!isEmpty(vouchers) ? (
              <>
                <div className="flex flex-col justify-between lg:flex-row">
                  <VoucherFilter
                    filterClassName="pt-3 pb-5 lg:pb-5"
                    labelClassName="font-bold"
                    hideFiltersOnDefault={false}
                  />
                  <div className="flex flex-col gap-2">
                    <BookOneOffSession buttonClassName="mt-3 h-auto rounded-full" />
                  </div>
                </div>
                {!isEmpty(vouchersInView) ? (
                  <div>
                    {vouchersInView.map((voucher) => (
                      <VoucherListItem
                        key={voucher.id}
                        voucher={voucher}
                        originalSession={fetchCancelledSessions.find(
                          (session) => session.studentSessionId === voucher.originalStudentSession
                        )}
                      />
                    ))}
                    <RecordsPagination
                      recordsTotal={filteredVouchers.length}
                      noOfRecordsPerPage={NO_OF_VOUCHERS_PER_PAGE}
                      handlePaginationClick={handlePaginationClick}
                      pageIndex={startIndex}
                    />
                  </div>
                ) : (
                  <div className="flex flex-col items-center justify-center gap-4 rounded-lg border-2 border-dashed border-grey-3 p-7">
                    <p className="text-lg font-bold">No vouchers found</p>
                    <p>Try adjusting your filter to find what you&apos;re looking for</p>
                  </div>
                )}
              </>
            ) : (
              <div className="flex flex-col items-center justify-center gap-4 rounded-lg border-2 border-dashed border-grey-3 p-7">
                <p className="text-lg font-bold">No vouchers available</p>
                <p>
                  Cluey Vouchers may take a few minutes to generate after cancelling a session, and will appear here
                  when available.
                </p>
              </div>
            )}
          </div>
        </>
      )}
    </HubPageLayout>
  );
};

export default withVoucherContextProvider(Vouchers);
